// What the open content becomes (결과물), next to the Studio pages it comes from. The
// outputs a content can become are listed by studio-families.mjs, one tab each. An output is
//
//   { id, path, noun,                              // 'pdf', 'pdf' (its address), 'PDF' — "PDF 8쪽"
//     subjectOf(content) -> { id, title } | null,  // what this content is, for this output
//     intro, linkedLabel, dataLabel,               // how its pages are described
//     published?(subject) -> { note, pages },      // the last published output, if it has one
//     edits?: { note, target, available(),         // text its pages print from outside the document (kept,
//       owns(address), label(address), list(),       //   once applied, in `target`: '원고'),
//       load(address, pending?), save({ address, base, value, reviewed }), revert(address) },
//     templatesOf?(pages) -> [{ id, title, layout, chapter, pages }]  // an output that lays out its own
//                                                  //   pages: its page templates, the Studio's page list; its
//                                                  //   pages are then edited where they print their text,
//     partKinds?: { kind: { label, note, color, outline? } },  // their parts drawn and explained by kind
//     layoutEdits?: { note, target, available(), list(), save({ address, base, value }), revert(address) },  // and the
//     layoutChoices?: { prop: { value: name } },   //   values of the layouts they are drawn from (틀 디자인)
//     documentEdits?({ document(), changed(address) }) -> edits  // the text they print from the Studio
//                                                  //   document itself, edited in it in place
//     describePreview?(result, changedCount),      // what a freshly built one is
//     workshop?: {                                 // only where a local server builds it
//       available() -> boolean, previewLabel, preview(document, onProgress) -> { title, pages, download? },
//       applyLabel?, apply?(document, onProgress) -> { changedFiles } },
//     browserPrint?(document),                     // print it with the browser's dialog
//     live?: true, actions?: [{ id, label, hook }] }  // drawn live (output-live.mjs)
//
// A page is { number, title, layout, image, large?, width?, height?, changed?, sectionId? | section?, sections?, chapter? }:
// the Studio page it comes from is `sectionId`, or found from the template `section` it is
// mainly made of (null when it is built from data); `sections` lists every template section
// it prints (the cover also carries the title and the 특징), so each of their Studio pages
// finds it. `chapter` is the part of the output outside the template the page belongs to
// ({ id, kind, name, title, note, pages }; a book's 대운, 세운, 부록): the page list groups the
// pages by it, and a page with no Studio page still says where it comes from.
// Pages are read one after another in 이어 보기 (output-reader.mjs); closing the panel keeps
// each output's last build and the page being read.
import { mountOutputReader } from './output-reader.mjs';
import { mountLiveOutput } from './output-live.mjs';

/** The Studio page (section id) that holds a template section, or null. */
export function studioPageFor(content, templateSection) {
  if (!templateSection) return null;
  const prefix = `${templateSection}.`;
  return content.sections.find(section => Object.values(section.origin?.elements ?? {}).some(role => role.startsWith(prefix)))?.id ?? null;
}

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}

const keyOf = ({ output, subject }) => `${output.id}|${subject.id}`;

/** 9–22쪽, or 1, 4–8쪽 when the pages are not all in a row. */
export function pageRange(numbers) {
  const runs = [];
  for (const number of numbers) {
    const last = runs.at(-1);
    if (last && number === last[1] + 1) last[1] = number;
    else runs.push([number, number]);
  }
  return `${runs.map(([from, to]) => (from === to ? String(from) : `${from}–${to}`)).join(', ')}쪽`;
}

/**
 * One page editor from several (the Studio document's, the output's): each address goes to the
 * editor that owns it and works here. save() says whether the text still differs from what is kept.
 */
export function joinEditors(editors) {
  const all = editors.filter(Boolean);
  let ready = new Map();
  const of = address => all.find(editor => ready.get(editor) && editor.owns(address));
  return {
    /** What the editors of `addresses` say about where their text is kept. */
    noteOf: addresses => [...new Set(addresses.map(of).filter(Boolean))].map(editor => editor.note).filter(Boolean).join(' '),
    async available() {
      ready = new Map(await Promise.all(all.map(async editor => [editor, await editor.available().catch(() => false)])));
      return [...ready.values()].some(Boolean);
    },
    owns: address => Boolean(of(address)),
    label: address => of(address)?.label(address) ?? null,
    async list() { return (await Promise.all(all.filter(editor => ready.get(editor)).map(editor => editor.list()))).flat(); },
    load: (address, pending) => of(address).load(address, pending),
    async save(edit) {
      const result = await of(edit.address).save(edit);
      return { pending: Array.isArray(result) ? result.some(item => item.address === edit.address) : Boolean(result?.pending) };
    },
    async revert(address) {
      const editor = of(address);
      await editor.revert(address);
      return { text: (await editor.load(address, await editor.list())).text };
    },
  };
}

/** The chapters of `pages`, each once, in the order they come. */
export function chaptersOf(pages) {
  return [...new Map(pages.filter(page => page.chapter).map(page => [page.chapter.id, page.chapter])).values()];
}

/**
 * The header button and its dialog. `outputsFor(content)` lists what the content can become;
 * `getDocument()` resolves the content with the live editor's latest text; `getActivePage()`
 * is the Studio page being edited; `selectPage(id)` shows a Studio page; `hooks` are what the
 * Studio lets an output's actions do (openCourse, openReading). Shown with `{ host }` the
 * panel sits in that element instead of over the Studio (the editor's 책 쪽으로 편집, 결과물로 보기);
 * `onLeave()` is then what 닫기 does, `onRead(sectionId, page)` hears the page being read and
 * which Studio page prints it, and `onTemplates(templates)` the page templates of the pages shown
 * (an output that lays out its own pages; [] otherwise). Such an output's pages are edited where
 * they print their text; `onDocumentEdited(address)` hears an edit made in the Studio document
 * (getContent(), in place — the Studio must not hold it open in an editor meanwhile).
 * `opensStudioPages()` false: the content is edited on its output's pages only, so no page offers its Studio page.
 */
export function mountOutputPanel({ outputsFor, getContent, getDocument = async () => getContent(), getActivePage = () => null, selectPage, hooks = {},
  onLeave = () => {}, onRead = () => {}, onView = () => {}, onTemplates = () => {}, onDocumentEdited = () => {}, opensStudioPages = () => true }) {
  const heading = node('h2', { id: 'es-output-title' });
  const note = node('p', { 'data-testid': 'studio-output-note' });
  const closeButton = node('button', { type: 'button', class: 'es-output-close', text: '닫기' });
  const kinds = node('div', { class: 'es-output-kinds', role: 'tablist', 'aria-label': '결과물 종류' });
  const filters = node('div', { class: 'es-output-filters', role: 'group', 'aria-label': '쪽 거르기' });
  const progress = node('p', { class: 'es-output-progress', 'data-testid': 'studio-output-progress', hidden: '' });
  // Text edited where the pages print it, waiting for a preview and 책에 반영.
  const pendingNote = node('p', { class: 'es-output-pending', 'data-testid': 'studio-output-pending', hidden: '' });
  const action = (testid, text = '') => node('button', { type: 'button', class: 'es-output-action', 'data-testid': testid, text, hidden: '' });
  const previewButton = action('studio-output-preview');
  const publishedButton = action('studio-output-published', '게시본 보기');
  const keptPreviewButton = action('studio-output-kept-preview', '만든 것 다시 보기');
  const applyButton = action('studio-output-apply');
  const downloadLink = node('a', { class: 'es-output-action', 'data-testid': 'studio-output-download', download: '', hidden: '' });
  const browserPrintButton = action('studio-output-browser-print', '브라우저로 인쇄 · PDF로 저장');
  const hookButtons = node('span', { class: 'es-output-hooks' });
  const workshopBar = node('div', { class: 'es-output-actions' }, [previewButton, publishedButton, keptPreviewButton, applyButton, downloadLink, browserPrintButton, hookButtons]);
  const readButton = node('button', { type: 'button', class: 'es-output-action', 'data-testid': 'studio-output-read', text: '처음부터 이어 보기' });
  const grid = node('div', { class: 'es-output-grid', 'data-testid': 'studio-output-grid' });
  // What each output was showing, kept across closing the panel: its last build for this
  // subject, which of published/built was shown, and the page being read.
  // key -> { preview: { result, madeAt } | null, showing, reading }
  const kept = new Map();
  // The tab picked last, remembered while the same content is open ({ subjectId, outputId }).
  let openDocument = null;
  let busy = false, choices = [], current = null, chosen = null, view = null, filter = 'all';
  const keptNow = () => (current ? kept.get(keyOf(current)) : null);
  // The page editor of an output whose pages are edited where they print (one per output): the
  // Studio document's text and the output's own.
  const pageEditors = new Map();
  function pageEditorOf(output) {
    if (!output.documentEdits && !output.edits) return null;
    if (!pageEditors.has(output)) {
      pageEditors.set(output, joinEditors([output.documentEdits?.({ document: getContent, changed: address => onDocumentEdited(address) }), output.edits]));
    }
    return pageEditors.get(output);
  }
  const designEditors = new Map();
  function designEditorOf(output) {
    if (!output.designEdits) return output.layoutEdits ?? null;
    if (!designEditors.has(output)) designEditors.set(output, output.designEdits({ document: getContent, changed: onDocumentEdited }));
    return designEditors.get(output);
  }
  async function refreshCanvas() {
    const number = reader.page;
    await showPublished();
    if (number) read(number);
  }
  const reader = mountOutputReader({
    onBack: () => showList(),
    onEdit: sectionId => void openStudioPage(sectionId),
    onMove: number => {
      const state = keptNow();
      if (state) state.reading = number;
      const page = view?.pages.find(item => item.number === number);
      if (page) onRead(targetOf(page), page);
      reportView();
    },
    onPageEdited: () => {
      void refreshPending();
      if (current.output.refreshOnEdit) void refreshCanvas().catch(error => { progress.hidden = false; progress.textContent = error.message; });
    },
    // 틀 디자인's 미리보기: the same preview, awaited there (the panel's own progress line is hidden while reading).
    onDesignPreview: async () => {
      if (busy) throw new Error('이미 만드는 중입니다.');
      busy = true; previewButton.disabled = true; applyButton.disabled = true;
      try { if (current.output.refreshOnEdit) await refreshCanvas(); else await buildPreview(() => {}); } finally { busy = false; previewButton.disabled = false; applyButton.disabled = false; }
    },
  });
  /**
   * Says how many texts and layout values (틀 디자인) wait for 미리보기 and 책에 반영, for an output whose
   * pages are edited in place, and where each is written (their editor's `target` and `note`).
   */
  function showPending(pending, designs = []) {
    const edits = current?.output.edits, layoutEdits = current && designEditorOf(current.output);
    const notes = [];
    if (pending?.length) notes.push(`고친 글 ${pending.length}개 · ‘${current.output.workshop?.previewLabel ?? '미리보기'}’로 쪽을 보고 ‘${current.output.workshop?.applyLabel ?? '반영'}’으로 ${edits.target}에 씁니다. ${edits.note}`);
    if (designs.length) notes.push(`캔버스 디자인 ${designs.length}곳 · 이 콘텐츠에 저장됨. ${layoutEdits.note}`);
    pendingNote.hidden = !notes.length;
    pendingNote.textContent = notes.join(' ');
  }
  async function refreshPending() {
    const edits = current?.output.edits, layoutEdits = current && designEditorOf(current.output);
    try { showPending(edits && await edits.available().catch(() => false) ? await edits.list() : [], layoutEdits ? await layoutEdits.list() : []); } catch { showPending([]); }
  }
  const live = mountLiveOutput({ onEdit: sectionId => void openStudioPage(sectionId) });
  const dialog = node('dialog', { class: 'es-output', 'aria-labelledby': 'es-output-title', 'data-testid': 'studio-output' }, [
    node('header', {}, [heading, closeButton, kinds, note, pendingNote, workshopBar, progress, filters]), grid, reader.element, live.element,
  ]);
  const inline = () => dialog.hasAttribute('data-inline');
  closeButton.addEventListener('click', () => { if (inline()) onLeave(); else dialog.close(); });
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  dialog.addEventListener('close', () => { live.close(); reader.close(); });
  // Escape while reading goes back to the list first.
  dialog.addEventListener('cancel', event => { if (reader.isOpen) { event.preventDefault(); showList(); } });
  readButton.addEventListener('click', () => read(view.pages[0]?.number));

  async function openStudioPage(sectionId) { if (!inline()) dialog.close(); await selectPage(sectionId); }
  /** Tells `onView` which output is shown and the page being read (null in the list). */
  function reportView() {
    if (current && dialog.open) onView({ output: current.output.path, page: reader.isOpen ? reader.page : null });
  }
  /** The Studio pages a book page prints, the one it is mainly made of first. */
  function targetsOf(page) {
    if (page.sectionId) return view.content.sections.some(section => section.id === page.sectionId) ? [page.sectionId] : [];
    return [...new Set((page.sections ?? [page.section]).map(section => studioPageFor(view.content, section)).filter(Boolean))];
  }
  const targetOf = page => targetsOf(page)[0] ?? null;
  const prints = (page, sectionId) => targetsOf(page).includes(sectionId);
  function read(number) {
    dialog.dataset.mode = 'reader';
    grid.hidden = true;
    const pageEditor = pageEditorOf(current.output);
    // The Studio page a page prints from, as the canvas numbers and names it (캔버스 7쪽 열기).
    const canvasPageOf = sectionId => {
      const at = view.content.sections.findIndex(section => section.id === sectionId);
      return at < 0 ? null : { number: at + 1, title: view.content.sections[at].title || `페이지 ${at + 1}` };
    };
    reader.open(view.pages, { start: number, targetOf, canvasPageOf, canOpenStudioPage: opensStudioPages(), labels: { linked: current.output.linkedLabel, data: current.output.dataLabel },
      editing: Boolean(pageEditor || designEditorOf(current.output)), pageEditor, templates: current.output.templatesOf?.(view.pages) ?? [], partKinds: current.output.partKinds ?? {},
      layoutEditor: designEditorOf(current.output), layoutChoices: current.output.layoutChoices ?? {}, designOptions: current.output.designOptions ?? {} });
    reportView();
  }
  function showList() {
    const state = keptNow();
    if (state) state.reading = null;
    if (dialog.dataset.mode === 'reader') delete dialog.dataset.mode;
    reader.close();
    grid.hidden = false;
    reportView();
  }

  function tile(page) {
    const target = targetOf(page);
    const editing = Boolean(view.activePage) && prints(page, view.activePage);
    const kind = target ? current.output.linkedLabel : page.chapter?.name ?? current.output.dataLabel;
    const badges = [...(kind ? [node('span', { class: 'es-output-page-kind', text: kind })] : []),
      ...(page.changed ? [node('span', { class: 'es-output-badge es-output-badge--changed', text: '바뀜' })] : []),
      ...(editing ? [node('span', { class: 'es-output-badge es-output-badge--current', text: '편집 중' })] : [])];
    const button = node('button', { type: 'button', class: ['es-output-page', target && 'es-output-page--editable', page.changed && 'es-output-page--changed', editing && 'es-output-page--current'].filter(Boolean).join(' '),
      'data-page': String(page.number), 'data-layout': page.layout, 'data-testid': `studio-output-page-${page.number}`,
      'aria-label': `${page.number}쪽 ${page.title}${page.changed ? ' · 바뀜' : ''}${kind ? ` · ${kind}` : ''}` }, [
      page.image ? node('img', { src: page.image, alt: '', loading: 'lazy', decoding: 'async', width: '150', height: '213' }) : node('span', { class: 'es-output-page-native-label', text: `${page.number}쪽 캔버스` }),
      node('span', { class: 'es-output-page-number', text: String(page.number) }),
      node('span', { class: 'es-output-page-title', text: page.title }),
      node('span', { class: 'es-output-page-badges' }, badges),
    ]);
    button.addEventListener('click', () => read(page.number));
    return button;
  }

  function render() {
    const { pages, activePage } = view;
    const counts = {
      all: pages.length,
      template: pages.filter(page => targetOf(page)).length,
      changed: pages.filter(page => page.changed).length,
      current: activePage ? pages.filter(page => prints(page, activePage)).length : 0,
    };
    // Only filters that pick out some of the pages are offered.
    const shown = { all: true, template: counts.template > 0 && counts.template < counts.all,
      changed: view.preview && pages.some(page => typeof page.changed === 'boolean'), current: counts.current > 0 && counts.current < counts.all };
    if (!shown[filter]) filter = 'all';
    const labels = { all: '모든 쪽', template: `${current.output.linkedLabel} 쪽`, changed: '바뀐 쪽', current: '편집 중인 쪽' };
    filters.replaceChildren(...(pages.length ? [readButton] : []), ...Object.keys(labels).filter(key => shown[key] && pages.length).map(key => {
      const button = node('button', { type: 'button', 'aria-pressed': String(key === filter), 'data-testid': `studio-output-filter-${key}` },
        [node('span', { text: labels[key] }), node('span', { class: 'es-output-count', text: String(counts[key]) })]);
      button.addEventListener('click', () => { filter = key; render(); });
      return button;
    }));
    const keep = { all: () => true, template: page => targetOf(page), changed: page => page.changed, current: page => prints(page, activePage) }[filter];
    const shownPages = pages.filter(keep);
    // Pages with chapters are listed part by part, each run of pages under what it is made of.
    const grouped = pages.some(page => page.chapter);
    const groupOf = page => page.chapter?.id ?? (targetOf(page) ? 'template' : 'data');
    grid.replaceChildren(...shownPages.flatMap((page, at) => {
      if (!grouped || (at > 0 && groupOf(shownPages[at - 1]) === groupOf(page))) return [tile(page)];
      const run = [];
      for (let next = at; next < shownPages.length && groupOf(shownPages[next]) === groupOf(page); next += 1) run.push(shownPages[next].number);
      return [groupHeading(page, run), tile(page)];
    }));
  }

  /** The heading of a run of pages: the chapter they are in, or the template's pages. */
  function groupHeading(page, numbers) {
    const { chapter } = page;
    const target = !chapter && Boolean(targetOf(page));
    const name = chapter ? chapter.name : target ? current.output.linkedLabel : current.output.dataLabel;
    const about = chapter ? `${chapter.title === chapter.name ? '' : `${chapter.title} — `}${chapter.note}`
      : target ? '제작실 페이지의 글로 만드는 쪽 · 누르면 그 쪽부터 이어 보고, 이 쪽 편집하기로 그 페이지를 엽니다' : current.output.dataNote ?? '';
    return node('h3', { class: 'es-output-group', 'data-kind': chapter ? 'chapter' : target ? 'template' : 'data', ...(chapter ? { 'data-chapter': chapter.kind } : {}),
      'data-testid': `studio-output-group-${chapter ? chapter.kind : target ? 'template' : 'data'}` }, [
      node('strong', { text: name }), node('span', { class: 'es-output-group-range', text: pageRange(numbers) }), node('small', { text: about }),
    ]);
  }

  // Which of the output's buttons apply to what is shown now.
  async function refreshActions() {
    const { output } = current;
    const local = Boolean(output.workshop) && await output.workshop.available();
    const state = keptNow();
    const preview = Boolean(view?.preview);
    previewButton.hidden = !local;
    previewButton.textContent = output.workshop?.previewLabel ?? '';
    applyButton.hidden = !local || !output.workshop.apply;
    applyButton.textContent = output.workshop?.applyLabel ?? '';
    publishedButton.hidden = !preview || !output.published;
    keptPreviewButton.hidden = preview || !state?.preview || Boolean(output.live);
    const download = preview ? state?.preview?.result.download : null;
    downloadLink.hidden = !download;
    if (download) { downloadLink.href = download; downloadLink.textContent = `${output.noun} 내려받기`; }
    browserPrintButton.hidden = !output.browserPrint;
    hookButtons.replaceChildren(...(output.actions ?? []).filter(item => hooks[item.hook]).map(item => {
      const button = node('button', { type: 'button', class: 'es-output-action', 'data-testid': `studio-output-hook-${item.id}`, text: item.label });
      button.addEventListener('click', () => { if (!inline()) dialog.close(); void hooks[item.hook](); });
      return button;
    }));
  }

  function showPages(pages, { preview }) {
    view = { pages, preview, content: getContent(), activePage: getActivePage() };
    onTemplates(current.output.templatesOf?.(pages) ?? []);
    showList();
    render();
  }

  function showPreview(result, madeAt, { resumed = false } = {}) {
    const { output } = current;
    const changed = result.pages.filter(page => page.changed).length;
    heading.textContent = `${result.title} · ${output.published ? '미리보기' : output.noun} ${result.pages.length}쪽`;
    note.textContent = `${resumed ? `${madeAt.toLocaleTimeString('ko-KR')}에 만든 것입니다. ` : ''}${output.describePreview(result, changed)} `
      + (resumed ? '그 뒤에 고친 글은 다시 만들어야 보입니다.' : output.workshop?.apply ? '아직 템플릿에 반영하지 않았습니다.' : '');
    if (changed && !resumed) filter = 'changed';
    kept.set(keyOf(current), { preview: { result, madeAt }, showing: 'preview', reading: null });
    showPages(result.pages, { preview: true });
  }

  async function showPublished() {
    const showing = current, content = getContent();
    const { output, subject } = showing;
    kept.set(keyOf(current), { preview: keptNow()?.preview ?? null, showing: 'published', reading: null });
    if (!output.published) {
      heading.textContent = `${subject.title} · ${output.noun}`;
      const local = Boolean(output.workshop) && await output.workshop.available();
      if (current !== showing || getContent() !== content) return;
      note.textContent = `${output.intro}${output.workshop && !local ? ' 이 화면에서는 파일을 바로 만들 수 없어 브라우저 인쇄로 저장합니다.' : ''}`;
      showPages([], { preview: false });
      return;
    }
    const published = await output.published(subject, content);
    if (current !== showing || getContent() !== content) return;
    const editable = published.pages.filter(page => studioPageFor(content, page.section)).length;
    const chapters = chaptersOf(published.pages);
    heading.textContent = `${subject.title} · ${output.noun} ${published.pages.length}쪽`;
    // An output that lays out its own pages is edited on them; otherwise a page is the template's
    // (opening its Studio page) or made from data.
    note.textContent = output.templatesOf
      ? `${published.note} 쪽 틀 ${output.templatesOf(published.pages).length}개로 짠 ${published.pages.length}쪽입니다`
        + `${chapters.length ? `(${chapters.map(chapter => `${chapter.name} ${pageRange(chapter.pages)}`).join(', ')} 포함)` : ''}. `
        + `쪽을 누르면 그 쪽을 열고, 오른쪽 ‘이 쪽의 글’에서 그 쪽이 싣는 글을 고칩니다. ${output.dataNote}`
      : output.canvas ? published.note : `${published.note} 쪽을 누르면 그 쪽부터 이어 봅니다. 테두리가 있는 ${editable}쪽은 이 템플릿의 글로 만들어져 '이 쪽 편집하기'로 그 편집 쪽을 엽니다. 나머지는 ${output.dataNote}`;
    showPages(published.pages, { preview: false });
  }

  async function showLive() {
    const { output, subject } = current;
    heading.textContent = `${subject.title} · ${output.noun}`;
    note.textContent = output.intro;
    view = { pages: [], preview: false, content: getContent(), activePage: getActivePage() };
    onTemplates([]);
    filters.replaceChildren(); grid.replaceChildren(); grid.hidden = true;
    dialog.dataset.mode = 'live';
    await live.open(await getDocument(), { startSectionId: getActivePage(), canEdit: opensStudioPages() });
  }

  /** Shows the chosen output: live, its last build, or its published pages. */
  async function showOutput() {
    reader.close(); live.close(); delete dialog.dataset.mode;
    grid.replaceChildren(); filters.replaceChildren(); grid.hidden = false; filter = 'all';
    for (const tab of kinds.children) tab.setAttribute('aria-selected', String(tab.dataset.output === current.output.id));
    // Where the pages are edited, the Studio's own toggle leaves them (for the canvas).
    closeButton.hidden = inline() && Boolean(current.output.templatesOf);
    const state = keptNow();
    const reading = state?.reading ?? null;
    if (current.output.live) await showLive();
    else if (state?.showing === 'preview' && state.preview) showPreview(state.preview.result, state.preview.madeAt, { resumed: true });
    else await showPublished();
    await refreshActions();
    await refreshPending();
    // Reading continues at the page left for editing; otherwise the pages printed from the
    // Studio page being edited come first into view.
    if (reading && !current.output.live) read(reading);
    else grid.querySelector('.es-output-page--current')?.scrollIntoView({ block: 'center' });
  }

  async function withProgress(label, action) {
    if (busy) return;
    busy = true; previewButton.disabled = true; applyButton.disabled = true;
    progress.hidden = false; progress.textContent = label;
    try {
      await action(line => { progress.textContent = `${label} ${line}`; });
    } catch (error) {
      progress.textContent = `실패 — ${error.message}`;
    } finally {
      busy = false; previewButton.disabled = false; applyButton.disabled = false;
    }
  }

  /** Builds the preview from the open edit; reading, it reads on at the same page, drawn anew. */
  async function buildPreview(onProgress) {
    const building = current;
    const result = await building.output.workshop.preview(await getDocument(), onProgress);
    progress.hidden = true;
    if (current !== building) return;
    const reading = reader.isOpen ? reader.page : null;
    showPreview(result, new Date());
    await refreshActions();
    if (reading && view.pages.some(page => page.number === reading)) read(reading);
  }
  previewButton.addEventListener('click', () => withProgress(`${current.output.noun} 만드는 중…`, buildPreview));
  publishedButton.addEventListener('click', async () => { await showPublished(); await refreshActions(); });
  keptPreviewButton.addEventListener('click', async () => {
    const state = keptNow();
    if (!state?.preview) return;
    showPreview(state.preview.result, state.preview.madeAt, { resumed: true });
    await refreshActions();
  });
  applyButton.addEventListener('click', () => withProgress(`${current.output.noun}에 반영하는 중…`, async onProgress => {
    const applying = current, target = getContent();
    if (!window.confirm(applying.output.workshop.applyConfirm)) { progress.hidden = true; return; }
    const submitted = structuredClone(await getDocument());
    if (current !== applying || getContent() !== target) { progress.hidden = true; return; }
    const result = await applying.output.workshop.apply(submitted, onProgress);
    designEditorOf(applying.output)?.rebaseApplied?.(submitted, target);
    // A long build may finish after the user has opened another document or output.
    // Rebase only its captured document; never replace the other document's reading state.
    if (current !== applying || getContent() !== target) { progress.hidden = true; return; }
    const reading = reader.isOpen ? reader.page : null;
    kept.delete(keyOf(applying));
    await showPublished();
    if (current !== applying || getContent() !== target) { progress.hidden = true; return; }
    await refreshPending();
    await refreshActions();
    if (current !== applying || getContent() !== target) { progress.hidden = true; return; }
    if (reading && view.pages.some(page => page.number === reading)) read(reading);
    progress.textContent = `반영 완료 — 바뀐 파일 ${result.changedFiles.length}개. 새 게시본을 불러왔습니다(커밋·운영 배포는 따로).`;
  }));
  browserPrintButton.addEventListener('click', () => withProgress('인쇄할 화면을 준비하는 중…', async () => {
    await current.output.browserPrint(await getDocument());
    progress.hidden = true;
  }));

  /**
   * Opens the panel over the Studio, or with `host` inside it. `output` picks the output by
   * its address name (book, pdf, lecture) and `page` the page to read; `read` starts reading at
   * the page the Studio page being edited prints (or the first page).
   */
  async function show({ host = null, read: startReading = false, output: outputPath = null, page: startPage = null } = {}) {
    if (openDocument !== getContent()) { kept.clear(); openDocument = getContent(); }
    choices = outputsFor(getContent());
    const sameContent = chosen && chosen.subjectId === choices[0]?.subject.id;
    current = (outputPath && choices.find(choice => choice.output.path === outputPath))
      || (sameContent && choices.find(choice => choice.output.id === chosen.outputId)) || choices[0] || null;
    if (current && outputPath) chosen = { subjectId: current.subject.id, outputId: current.output.id };
    kinds.replaceChildren(...(choices.length > 1 ? choices : []).map(choice => {
      const tab = node('button', { type: 'button', role: 'tab', 'data-output': choice.output.id, 'data-testid': `studio-output-kind-${choice.output.id}`, text: choice.output.noun });
      tab.addEventListener('click', () => {
        if (current === choice) return;
        chosen = { subjectId: choices[0].subject.id, outputId: choice.output.id }; current = choice;
        void showOutput().then(reportView);
      });
      return tab;
    }));
    if (!busy) progress.hidden = true;
    heading.textContent = current ? `${current.subject.title} · ${current.output.noun}` : '결과물';
    note.textContent = '불러오는 중…';
    const place = host ?? document.body;
    if (dialog.open && inline() !== Boolean(host)) dialog.close();
    if (dialog.parentElement !== place) place.append(dialog);
    dialog.toggleAttribute('data-inline', Boolean(host));
    closeButton.textContent = host ? '편집으로 돌아가기' : '닫기';
    if (!dialog.open) { if (host) dialog.show(); else dialog.showModal(); }
    if (!current) {
      reader.close(); live.close(); delete dialog.dataset.mode;
      heading.textContent = '결과물';
      note.textContent = '이 콘텐츠에는 아직 쪽이 없어 만들 결과물이 없습니다. 쪽을 더하면 PDF와 강의 화면으로 볼 수 있습니다.';
      for (const button of [previewButton, publishedButton, keptPreviewButton, applyButton, downloadLink, browserPrintButton]) button.hidden = true;
      hookButtons.replaceChildren(); filters.replaceChildren(); grid.replaceChildren();
      onTemplates([]);
      return;
    }
    await showOutput();
    if (!current.output.live && view.pages.some(page => page.number === startPage)) read(startPage);
    else if (startReading && !current.output.live && reader.isOpen === false && view.pages.length) {
      const active = getActivePage();
      read((view.pages.find(page => active && prints(page, active)) ?? view.pages[0]).number);
    } else reportView();
  }

  /** Brings the first page printed from Studio page `sectionId` into view, reading from it. */
  function reveal(sectionId) {
    const page = view?.pages.find(item => prints(item, sectionId));
    if (page) read(page.number);
    return Boolean(page);
  }
  /** Reads from the page numbered `number`, when the pages shown have it. */
  function revealPage(number) {
    if (reader.isOpen && reader.goTo(number)) return true;
    const page = view?.pages.find(item => item.number === number);
    if (page) read(page.number);
    return Boolean(page);
  }
  function leave() { reader.close(); live.close(); if (dialog.open) dialog.close(); dialog.remove(); }
  function dispose() { leave(); reader.dispose(); }

  const trigger = node('button', { type: 'button', class: 'es-output-open', 'data-testid': 'studio-output-open', text: '결과물' });
  /** Names on the button what the open content can become, e.g. '결과물 · 책·PDF·강의 화면'. */
  function update() {
    const found = outputsFor(getContent());
    trigger.textContent = found.length ? `결과물 · ${found.map(choice => choice.output.noun).join('·')}` : '결과물';
    trigger.title = found.length ? `${found[0].subject.title}: ${found.map(choice => choice.output.noun).join(', ')}` : '이 콘텐츠에는 아직 결과물이 없습니다';
  }
  update();
  return { trigger, dialog, show, update, reveal, revealPage, leave, dispose, flush: () => reader.flush() };
}
